import React, { useEffect, useRef } from 'react'
import { View, StyleSheet, TouchableOpacity, Animated } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text, Icon } from '../atoms'
import { useLanguage } from '@context'
import { useTooltipDismiss } from '../../hooks/useTooltipDismiss'

interface FeatureTooltipProps {
  id: string
  title?: string
  description: string
  icon?: string
  position?: 'top' | 'bottom'
  compact?: boolean
  style?: any
}

export const FeatureTooltip: React.FC<FeatureTooltipProps> = ({
  id,
  title,
  description,
  icon,
  position = 'top',
  compact = false,
  style,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const { visible, dismiss } = useTooltipDismiss(id)
  const fadeAnim = useRef(new Animated.Value(0)).current

  useEffect(() => {
    if (visible) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start()
    }
  }, [visible, fadeAnim])

  const handleDismiss = () => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(() => dismiss())
  }

  if (!visible) return null

  if (compact) {
    return (
      <Animated.View style={[styles.compactContainer, { opacity: fadeAnim }, style]}>
        <View style={styles.compactContent}>
          <View style={styles.compactIconContainer}>
            {icon ? (
              <Icon name={icon as any} size={12} color={theme.colors.white} />
            ) : (
              <Text type="body" style={styles.compactIconFallback}>?</Text>
            )}
          </View>
          <Text type="body" style={styles.compactText} numberOfLines={2}>{description}</Text>
          <TouchableOpacity onPress={handleDismiss} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Icon name="cross" size={10} color="rgba(255,255,255,0.7)" />
          </TouchableOpacity>
        </View>
      </Animated.View>
    )
  }

  return (
    <Animated.View
      style={[
        styles.container,
        position === 'bottom' && styles.containerBottom,
        { opacity: fadeAnim },
        style,
      ]}
    >
      <View style={styles.content}>
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            {icon ? (
              <Icon name={icon as any} size={14} color={theme.colors.white} />
            ) : (
              <Text type="body" style={styles.iconFallback}>?</Text>
            )}
          </View>
          {title ? <Text type="body" style={styles.title}>{title}</Text> : <View style={{ flex: 1 }} />}
          <TouchableOpacity onPress={handleDismiss} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Icon name="cross" size={12} color="rgba(255,255,255,0.7)" />
          </TouchableOpacity>
        </View>
        <Text type="body" style={styles.description}>{description}</Text>
        <TouchableOpacity style={styles.gotItButton} onPress={handleDismiss}>
          <Text type="body" style={styles.gotItText}>{t('featureTooltip.gotIt')}</Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      marginHorizontal: 16,
      marginBottom: theme.spacing.md,
      borderRadius: 12,
      backgroundColor: theme.colors.primary,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    containerBottom: {},
    content: {
      padding: 14,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 6,
    },
    iconContainer: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: 'rgba(255,255,255,0.2)',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 8,
    },
    iconFallback: {
      color: '#fff',
      fontSize: 13,
      fontWeight: '700',
    },
    title: {
      flex: 1,
      color: '#fff',
      fontSize: 14,
      fontWeight: '700',
    },
    description: {
      color: 'rgba(255,255,255,0.9)',
      fontSize: 13,
      lineHeight: 18,
      marginLeft: 32,
    },
    gotItButton: {
      alignSelf: 'flex-end',
      marginTop: 8,
      paddingHorizontal: 14,
      paddingVertical: 6,
      borderRadius: 14,
      backgroundColor: 'rgba(255,255,255,0.2)',
    },
    gotItText: {
      color: '#fff',
      fontSize: 12,
      fontWeight: '600',
    },

    // Compact variant (for inline/small spaces)
    compactContainer: {
      marginHorizontal: 8,
      marginVertical: 4,
      borderRadius: 8,
      backgroundColor: theme.colors.primary,
    },
    compactContent: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 10,
      paddingVertical: 8,
      gap: 8,
    },
    compactIconContainer: {
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: 'rgba(255,255,255,0.2)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    compactIconFallback: {
      color: '#fff',
      fontSize: 11,
      fontWeight: '700',
    },
    compactText: {
      flex: 1,
      color: 'rgba(255,255,255,0.9)',
      fontSize: 12,
      lineHeight: 16,
    },
  })
